import { faEnvelopeOpen, faPaperclip, faReply } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useModalManager from '@hooks/useModalManager';
import { Badge, Tooltip } from '@ui';
import { Column, DataTable, DataTableContainer, TableActionButton } from 'components/DataTable';
import moment from 'moment';
import dynamic from 'next/dynamic';
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSWRConfig } from 'swr';
import { InboxListItem } from './types';

const InboxMessage = dynamic(() => import('./InboxMessage'));

interface Props {
    clubSlug: string;
    clubMemberId: number;
}

/** The inbox mails one member sent, for the member preview. Archived ones included. */
export const MemberInboxList = ({ clubSlug, clubMemberId }: Props) => {
    const { t } = useTranslation('message-management');
    const { mutate } = useSWRConfig();
    const { addModal, removeModal } = useModalManager();
    const asyncList = useRef<any>(undefined);
    const countKey = `club/${clubSlug}/inbox/unread/count`;

    const handleChanged = useCallback(
        (item: InboxListItem) => {
            asyncList.current?.update(item.id, item);
            void mutate(countKey);
        },
        [countKey, mutate]
    );

    // archived mails stay in this list, only their state changed
    const handleRemoved = useCallback(() => {
        asyncList.current?.reload();
        void mutate(countKey);
    }, [countKey, mutate]);

    const openMail = useCallback(
        (item: InboxListItem) =>
            addModal({
                title: t('inbox.detailTitle'),
                content: (id: string) => (
                    <InboxMessage
                        clubSlug={clubSlug}
                        mailId={item.id}
                        onChange={handleChanged}
                        onRemove={handleRemoved}
                        onClose={() => removeModal(id)}
                    />
                ),
                size: 70,
                show: true
            }),
        [addModal, clubSlug, handleChanged, handleRemoved, removeModal, t]
    );

    const columns: Column[] = useMemo(
        () => [
            {
                name: t('inbox.columns.subject'),
                uid: 'subject',
                renderer: (_value, item: InboxListItem) => (
                    <button
                        type="button"
                        onClick={() => openMail(item)}
                        className="tw-flex tw-w-full tw-cursor-pointer tw-flex-col tw-border-0 tw-bg-transparent tw-p-0 tw-text-left"
                    >
                        <span className="tw-flex tw-items-center tw-gap-2">
                            {!item.read_at && (
                                <span className="tw-h-2 tw-w-2 tw-shrink-0 tw-rounded-full tw-bg-success" />
                            )}
                            <span className={item.read_at ? '' : 'tw-font-semibold'}>
                                {item.subject || t('inbox.noSubject')}
                            </span>
                            {item.has_attachments && (
                                <FontAwesomeIcon icon={faPaperclip} className="tw-text-accents-6" />
                            )}
                            {item.replied_at && (
                                <Tooltip
                                    tooltip={t('inbox.repliedAt', { date: moment(item.replied_at).format('lll') })}
                                >
                                    <FontAwesomeIcon icon={faReply} className="tw-text-accents-6" />
                                </Tooltip>
                            )}
                            {item.archived_at && (
                                <Badge color="light" flat size="xs">
                                    {t('inbox.folders.archive')}
                                </Badge>
                            )}
                        </span>
                        {item.preview && (
                            <span className="tw-line-clamp-1 tw-text-xs tw-text-accents-6">{item.preview}</span>
                        )}
                    </button>
                )
            },
            {
                name: t('inbox.columns.receivedAt'),
                uid: 'received_at',
                renderer: (value) => <span className="tw-whitespace-nowrap">{moment(value).format('lll')}</span>
            },
            { name: t('list.columns.actions'), uid: 'actions', align: 'end' }
        ],
        [openMail, t]
    );

    return (
        <DataTableContainer>
            <DataTable
                hideSearchBar
                className="tw-h-full"
                asyncListRef={asyncList}
                fetchUrl={`club-member/${clubMemberId}/inbound-messages`}
                columns={columns}
                tableProps={{ showSelectionCheckboxes: false, sticked: true, compact: false }}
                rowActions={(item: InboxListItem) => (
                    <TableActionButton.Container justify="flex-end">
                        <TableActionButton
                            icon={faEnvelopeOpen}
                            color="light"
                            tooltip={t('inbox.open')}
                            onPress={() => openMail(item)}
                        />
                    </TableActionButton.Container>
                )}
            />
        </DataTableContainer>
    );
};
